import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js"
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Check, X, Zap, Building2, Sparkles, CreditCard, FileText, AlertTriangle } from "lucide-react"
import { useTheme } from "../components/theme"

const FEATURES: { label: string; free: string | boolean; pro: string | boolean; business: string | boolean }[] = [
  { label: "Workspaces",                    free: "1",         pro: "Unlimited",   business: "Unlimited" },
  { label: "Members",                       free: "3",         pro: "10",          business: "Unlimited" },
  { label: "Projects",                      free: "3",         pro: "Unlimited",   business: "Unlimited" },
  { label: "Boards",                        free: "Unlimited", pro: "Unlimited",   business: "Unlimited" },
  { label: "Cards",                         free: "Unlimited", pro: "Unlimited",   business: "Unlimited" },
  { label: "Board, List & Calendar views",  free: true,        pro: true,          business: true },
  { label: "Timeline (Gantt) view",         free: false,       pro: true,          business: true },
  { label: "Swimlanes view",                free: true,        pro: true,          business: true },
  { label: "Board templates",               free: true,        pro: true,          business: true },
  { label: "Multiple assignees",            free: true,        pro: true,          business: true },
  { label: "Card cover colors",             free: true,        pro: true,          business: true },
  { label: "Card cover images",             free: false,       pro: true,          business: true },
  { label: "Story points",                  free: true,        pro: true,          business: true },
  { label: "Recurring cards",               free: false,       pro: true,          business: true },
  { label: "CSV import & export",           free: "Export only", pro: true,        business: true },
  { label: "Due date filters",              free: true,        pro: true,          business: true },
  { label: "Comment editing",               free: true,        pro: true,          business: true },
  { label: "Emoji reactions on comments",   free: true,        pro: true,          business: true },
  { label: "Card labels with colors",       free: true,        pro: true,          business: true },
  { label: "Column color indicators",       free: true,        pro: true,          business: true },
  { label: "Board background colors",       free: "8 presets", pro: "Full palette", business: "Full palette" },
  { label: "Custom hex colors",             free: false,       pro: true,          business: true },
  { label: "Gradient board backgrounds",    free: false,       pro: true,          business: true },
  { label: "File attachments",              free: false,       pro: true,          business: true },
  { label: "Workspace audit log",           free: false,       pro: true,          business: true },
  { label: "Webhooks / Zapier",             free: false,       pro: true,          business: true },
  { label: "Card followers",                free: true,        pro: true,          business: true },
  { label: "2FA security",                  free: true,        pro: true,          business: true },
  { label: "Email notifications",           free: true,        pro: true,          business: true },
  { label: "Priority support",              free: false,       pro: false,         business: true },
]

function FeatureValue({ val }: { val: string | boolean }) {
  if (val === true) return <Check className="mx-auto h-4 w-4 text-primary" />
  if (val === false) return <X className="mx-auto h-4 w-4 text-muted-foreground/40" />
  return <span className="text-sm font-medium">{val}</span>
}

function PaymentForm({ workspaceId, plan, address, amount, onSuccess, onCancel, setError }: {
  workspaceId: string
  plan: "pro" | "business"
  address: AddressInput | null
  amount: string
  onSuccess: () => void
  onCancel: () => void
  setError: (msg: string) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)

  const subscribe = useMutation({
    mutationFn: () =>
      fetcher<{ clientSecret?: string; active?: boolean; upgraded?: boolean; error?: string }>(`/api/billing/workspace/${workspaceId}/subscribe`, {
        method: "POST",
        body: JSON.stringify({ plan, address }),
      }),
    onSuccess: async (data) => {
      if (data.active || data.upgraded) {
        onSuccess()
        return
      }
      if (data.clientSecret && stripe && elements) {
        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          clientSecret: data.clientSecret,
          confirmParams: { return_url: `${window.location.origin}/workspaces/${workspaceId}/billing?success=1` },
          redirect: "if_required",
        })
        if (confirmError) {
          setError(confirmError.message ?? "Payment failed. Please try again.")
          setProcessing(false)
        } else {
          onSuccess()
        }
      } else {
        setError(data.error || "Failed to initialize payment")
        setProcessing(false)
      }
    },
    onError: (err: any) => {
      setError(err.message || "Subscription failed")
      setProcessing(false)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements || !address) return
    setProcessing(true)
    setError("")
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setError(submitError.message ?? "Please check your payment details")
      setProcessing(false)
      return
    }
    subscribe.mutate()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={processing}>Cancel</Button>
        <Button type="submit" className="flex-1" isLoading={processing} disabled={!stripe || !elements || !address || processing}>
          <CreditCard className="mr-2 h-4 w-4" /> Pay {amount}
        </Button>
      </div>
    </form>
  )
}

function SetupForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState("")
  const [processing, setProcessing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError("")
    const { error: stripeError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    })
    if (stripeError) {
      setError(stripeError.message ?? "Failed to update payment method.")
      setProcessing(false)
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={processing}>Cancel</Button>
        <Button type="submit" className="flex-1" isLoading={processing} disabled={!stripe || processing}>
          <CreditCard className="mr-2 h-4 w-4" /> Save card
        </Button>
      </div>
    </form>
  )
}

function PaymentModal({ workspaceId, plan, planLabel, publishableKey, onSuccess, onCancel }: {
  workspaceId: string
  plan: "pro" | "business"
  planLabel: string
  publishableKey: string
  onSuccess: () => void
  onCancel: () => void
}) {
  const { theme } = useTheme()
  const isDark = theme === "dark"
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey])
  const [error, setError] = useState("")
  const [address, setAddress] = useState<AddressInput | null>(null)
  const [subtotal, setSubtotal] = useState(plan === "pro" ? 999 : 299)
  const [tax, setTax] = useState(0)
  const [amount, setAmount] = useState(plan === "pro" ? 999 : 299)
  const [currency, setCurrency] = useState("usd")
  const estimateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const estimateTax = useMutation({
    mutationFn: (addr: AddressInput) =>
      fetcher<{ subtotal: number; tax: number; total: number; currency: string }>(`/api/billing/workspace/${workspaceId}/estimate-tax`, {
        method: "POST",
        body: JSON.stringify({ plan, address: addr }),
      }),
    onSuccess: (data) => {
      setSubtotal(data.subtotal)
      setTax(data.tax)
      setAmount(data.total)
      setCurrency(data.currency)
      setError("")
    },
    onError: (err: any) => {
      setError(err.message || "Failed to calculate tax")
    },
  })

  const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(n / 100)

  const elementsOptions = useMemo(() => ({
    mode: "subscription" as const,
    currency,
    amount,
    appearance: { theme: (isDark ? "night" : "stripe") as "night" | "stripe", variables: { colorPrimary: "#2563eb", borderRadius: "8px" } },
  }), [currency, amount, isDark])

  const handleAddressChange = (e: StripeAddressElementChangeEvent) => {
    console.log("[AddressElement] onChange", { complete: e.complete, value: e.value })
    if (e.value?.address) {
      const addr = e.value.address as AddressInput
      setAddress(addr)
      if (estimateTimeout.current) clearTimeout(estimateTimeout.current)
      estimateTimeout.current = setTimeout(() => {
        console.log("[estimate-tax] calling", addr)
        estimateTax.mutate(addr)
      }, 800)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-xl space-y-5">
        <div>
          <h2 className="text-lg font-semibold">Upgrade to {planLabel}</h2>
          <p className="text-sm text-muted-foreground mt-1">Enter your billing address and payment details below.</p>
        </div>

        <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{planLabel} plan</span>
            <span className="font-semibold">{fmt(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Tax</span>
            <span className="font-semibold">{estimateTax.isPending ? "Calculating..." : fmt(tax)}</span>
          </div>
          <div className="border-t border-border" />
          <div className="flex items-center justify-between">
            <span className="font-semibold">Total due today</span>
            <span className="text-lg font-bold">{fmt(amount)}</span>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Elements stripe={stripePromise} options={elementsOptions}>
          <div className="space-y-5">
            <ElementsUpdater amount={amount} />
            <div className="space-y-2">
              <p className="text-sm font-medium">Billing address</p>
              <AddressElement options={{ mode: "billing" }} onChange={handleAddressChange} />
            </div>
            <PaymentForm
              workspaceId={workspaceId}
              plan={plan}
              address={address}
              amount={fmt(amount)}
              onSuccess={onSuccess}
              onCancel={onCancel}
              setError={setError}
            />
          </div>
        </Elements>
      </div>
    </div>
  )
}

function ElementsUpdater({ amount }: { amount: number }) {
  const elements = useElements()
  useEffect(() => {
    if (elements) {
      elements.update({ amount })
    }
  }, [amount, elements])
  return null
}

type AddressInput = {
  line1?: string
  line2?: string
  city?: string
  state?: string
  postal_code?: string
  country: string
}

/** Billing page. Shows the current plan, a feature comparison table, and buttons to upgrade or manage the subscription via Stripe. */
export default function BillingPage() {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId/billing" })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: session, isPending: sessionLoading } = authClient.useSession()
  const queryClient = useQueryClient()
  const [selectedPlan, setSelectedPlan] = useState<"pro" | "business" | null>(null)
  const params = new URLSearchParams(window.location.search)
  const success = params.has("success")

  useEffect(() => {
    if (success) {
      queryClient.invalidateQueries({ queryKey: ["billing", workspaceId] })
    }
  }, [success, workspaceId, queryClient])

  const { data: billing, isLoading } = useQuery({
    queryKey: ["billing", workspaceId],
    queryFn: () => fetcher<{ subscription: { plan: string; status: string; currentPeriodEnd?: string } | null; publishableKey: string }>(`/api/billing/workspace/${workspaceId}`),
    enabled: !!session,
  })

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ id: string; name: string; role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })
  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  const [showUpdatePayment, setShowUpdatePayment] = useState(false)
  const [updatePaymentSecret, setUpdatePaymentSecret] = useState<{ clientSecret: string; publishableKey: string } | null>(null)
  const [showCancel, setShowCancel] = useState(false)
  const [showInvoices, setShowInvoices] = useState(false)
  const { theme } = useTheme()
  const isDark = theme === "dark"

  const cancel = useMutation({
    mutationFn: () => fetcher<{ ok: boolean }>(`/api/billing/workspace/${workspaceId}/cancel`, { method: "POST" }),
    onSuccess: () => { toast("Subscription will cancel at period end.", "success"); queryClient.invalidateQueries({ queryKey: ["billing", workspaceId] }); setShowCancel(false) },
    onError: (err: any) => toast(err.message || "Failed to cancel", "error"),
  })

  const resume = useMutation({
    mutationFn: () => fetcher<{ ok: boolean }>(`/api/billing/workspace/${workspaceId}/resume`, { method: "POST" }),
    onSuccess: () => { toast("Subscription resumed.", "success"); queryClient.invalidateQueries({ queryKey: ["billing", workspaceId] }) },
    onError: (err: any) => toast(err.message || "Failed to resume", "error"),
  })

  const startUpdatePayment = useMutation({
    mutationFn: () => fetcher<{ clientSecret: string; publishableKey: string }>(`/api/billing/workspace/${workspaceId}/update-payment`, { method: "POST" }),
    onSuccess: (data) => { setUpdatePaymentSecret(data); setShowUpdatePayment(true) },
    onError: (err: any) => toast(err.message || "Failed to start payment update", "error"),
  })

  const { data: invoiceData } = useQuery({
    queryKey: ["invoices", workspaceId],
    queryFn: () => fetcher<{ invoices: { id: string; date: number; amount: number; currency: string; status: string | null; pdf: string | null; number: string | null }[] }>(`/api/billing/workspace/${workspaceId}/invoices`),
    enabled: showInvoices && !!session,
  })

  if (!session && !sessionLoading) { navigate({ to: "/login" }); return null }
  if (sessionLoading || isLoading) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-64 space-y-3"><Skeleton className="h-8 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>
    </div>
  )

  const plan = billing?.subscription?.plan || "free"
  const isPro = plan === "pro"
  const isBusiness = plan === "business"
  const isPaid = isPro || isBusiness

  return (
    <Shell
      title="Billing"
      back={{ to: "/workspaces/$workspaceId", label: "Workspace" }}
      tabs={
        <div className="flex gap-4 text-sm">
          <Link to="/workspaces/$workspaceId" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Projects</Link>
          <Link to="/workspaces/$workspaceId/members" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Members</Link>
          <span className="border-b-2 border-primary pb-2 font-medium">Billing</span>
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/audit" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Audit log</Link>
          )}
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/webhooks" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Webhooks</Link>
          )}
        </div>
      }
    >
      <main className="mx-auto max-w-5xl space-y-8 p-6">
        {selectedPlan && billing?.publishableKey && (
          <PaymentModal
            workspaceId={workspaceId}
            plan={selectedPlan}
            planLabel={selectedPlan === "pro" ? "Pro" : "Business"}
            publishableKey={billing.publishableKey}
            onSuccess={() => {
              setSelectedPlan(null)
              toast("Payment successful! Your plan is now active.", "success")
              // Poll until the webhook updates the plan in our database.
              let attempts = 0
              const interval = setInterval(async () => {
                attempts++
                await queryClient.invalidateQueries({ queryKey: ["billing", workspaceId] })
                const latest = queryClient.getQueryData<{ subscription?: { plan?: string } | null }>(["billing", workspaceId])
                const currentPlan = latest?.subscription?.plan
                if (currentPlan === selectedPlan || attempts >= 12) {
                  clearInterval(interval)
                }
              }, 2500)
            }}
            onCancel={() => setSelectedPlan(null)}
          />
        )}
        {success && <p className="rounded-xl bg-green-100 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">✓ Payment successful. Your subscription is now active.</p>}

        {/* Update payment method modal */}
        {showUpdatePayment && updatePaymentSecret && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl space-y-5">
              <div>
                <h2 className="text-lg font-semibold">Update payment method</h2>
                <p className="text-sm text-muted-foreground mt-1">Your new card will be used for future charges.</p>
              </div>
              <Elements
                stripe={loadStripe(updatePaymentSecret.publishableKey)}
                options={{ clientSecret: updatePaymentSecret.clientSecret, appearance: { theme: isDark ? "night" : "stripe", variables: { colorPrimary: "#2563eb", borderRadius: "8px" } } }}
              >
                <SetupForm onSuccess={() => { setShowUpdatePayment(false); setUpdatePaymentSecret(null); toast("Payment method updated.", "success") }} onCancel={() => { setShowUpdatePayment(false); setUpdatePaymentSecret(null) }} />
              </Elements>
            </div>
          </div>
        )}

        {/* Cancel confirm dialog */}
        {showCancel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
            <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-xl space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500 mt-0.5" />
                <div>
                  <h2 className="text-base font-semibold">Cancel subscription?</h2>
                  <p className="text-sm text-muted-foreground mt-1">Your plan stays active until the end of the billing period. You can resume anytime before then.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowCancel(false)}>Keep plan</Button>
                <Button variant="destructive" className="flex-1" onClick={() => cancel.mutate()} isLoading={cancel.isPending}>Yes, cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Current plan banner */}
        <Card className="rounded-xl border-border bg-card shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Current plan</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-2xl font-bold capitalize">{plan}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${billing?.subscription?.status === "canceling" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" : "bg-primary/10 text-primary"}`}>
                    {billing?.subscription?.status === "canceling" ? "Canceling" : billing?.subscription?.status || "active"}
                  </span>
                </div>
                {billing?.subscription?.currentPeriodEnd && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {billing.subscription.status === "canceling" ? "Access until" : "Renews"}{" "}
                    {new Date(billing.subscription.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
              {!isPaid && <p className="text-sm text-muted-foreground">Pick a plan below to get more.</p>}
            </div>
            {isPaid && (
              <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                <Button size="sm" variant="outline" onClick={() => startUpdatePayment.mutate()} isLoading={startUpdatePayment.isPending}>
                  <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Update payment method
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowInvoices(v => !v)}>
                  <FileText className="mr-1.5 h-3.5 w-3.5" /> {showInvoices ? "Hide" : "View"} invoices
                </Button>
                {billing?.subscription?.status === "canceling" ? (
                  <Button size="sm" variant="outline" onClick={() => resume.mutate()} isLoading={resume.isPending}>Resume subscription</Button>
                ) : (
                  <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setShowCancel(true)}>Cancel subscription</Button>
                )}
              </div>
            )}
            {showInvoices && (
              <div className="border-t border-border pt-4">
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-3">Invoice history</p>
                {!invoiceData ? (
                  <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
                ) : invoiceData.invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No invoices yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {invoiceData.invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground">{new Date(inv.date * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                          <span className="font-medium">${(inv.amount / 100).toFixed(2)} {inv.currency.toUpperCase()}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${inv.status === "paid" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-muted text-muted-foreground"}`}>{inv.status}</span>
                        </div>
                        {inv.pdf && <a href={inv.pdf} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Download</a>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier cards */}
        <div className="grid items-stretch gap-5 md:grid-cols-3">
          {/* Free */}
          <Card className={`flex h-full flex-col rounded-xl border-border bg-card shadow-sm ${plan === "free" ? "ring-2 ring-primary/30" : ""}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted"><Sparkles className="h-4 w-4 text-muted-foreground" /></div>
                <CardTitle className="text-base font-semibold">Free</CardTitle>
                {plan === "free" && <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Current</span>}
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-4">
              <div className="text-3xl font-bold">$0<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
              <ul className="flex-1 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />1 workspace</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Up to 3 members</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />3 projects</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Unlimited boards & cards</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Board, List & Calendar views</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Card labels & column colors</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />8 preset board backgrounds</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />2FA & notifications</li>
                <li className="flex items-center gap-2"><X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />Timeline (Gantt) view</li>
                <li className="flex items-center gap-2"><X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />Custom colors & gradients</li>
                <li className="flex items-center gap-2"><X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />File attachments</li>
                <li className="flex items-center gap-2"><X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />Audit log</li>
              </ul>
              <Button variant="outline" size="sm" className="mt-auto w-full" disabled>Free forever</Button>
            </CardContent>
          </Card>

          {/* Pro */}
          <Card className={`relative flex h-full flex-col overflow-hidden rounded-xl border-primary/30 bg-gradient-to-b from-primary/5 to-transparent shadow-md ${isPro ? "ring-2 ring-primary/50" : ""}`}>
            {!isPro && (
              <div className="absolute right-3 top-3">
                <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">Most popular</span>
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10"><Zap className="h-4 w-4 text-primary" /></div>
                <CardTitle className="text-base font-semibold">Pro</CardTitle>
                {isPro && <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Current</span>}
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-4">
              <div><span className="text-3xl font-bold">$9.99</span><span className="text-sm font-normal text-muted-foreground">/workspace/mo</span></div>
              <ul className="flex-1 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Everything in Free</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Unlimited workspaces & projects</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Up to 10 members</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Timeline (Gantt) view</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Full color palette & custom hex</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Gradient board backgrounds</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />File attachments</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Workspace audit log</li>
                <li className="flex items-center gap-2"><X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />Priority support</li>
              </ul>
              {isPro ? (
                <Button variant="outline" size="sm" className="mt-auto w-full" onClick={() => setShowCancel(true)}>Cancel subscription</Button>
              ) : isBusiness ? (
                <Button size="sm" className="mt-auto w-full" onClick={() => setSelectedPlan("pro")}>Switch to Pro</Button>
              ) : (
                <Button size="sm" className="mt-auto w-full" onClick={() => setSelectedPlan("pro")}>Upgrade to Pro</Button>
              )}
            </CardContent>
          </Card>

          {/* Business */}
          <Card className={`flex h-full flex-col rounded-xl border-border bg-card shadow-sm ${isBusiness ? "ring-2 ring-primary/30" : ""}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted"><Building2 className="h-4 w-4 text-muted-foreground" /></div>
                <CardTitle className="text-base font-semibold">Business</CardTitle>
                {isBusiness && <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Current</span>}
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-4">
              <div><span className="text-3xl font-bold">$2.99</span><span className="text-sm font-normal text-muted-foreground">/seat/mo</span></div>
              <ul className="flex-1 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Everything in Pro</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" /><strong className="text-foreground">Unlimited members</strong></li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Unlimited workspaces & projects</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />Timeline, colors & gradients</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" />File attachments & audit log</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-primary" /><strong className="text-foreground">Priority support</strong></li>
              </ul>
              {isBusiness ? (
                <Button variant="outline" size="sm" className="mt-auto w-full" onClick={() => setShowCancel(true)}>Cancel subscription</Button>
              ) : isPro ? (
                <Button size="sm" className="mt-auto w-full" onClick={() => setSelectedPlan("business")}>Upgrade to Business</Button>
              ) : (
                <Button variant="outline" size="sm" className="mt-auto w-full" onClick={() => setSelectedPlan("business")}>Upgrade to Business</Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Full comparison table */}
        <Card className="overflow-hidden rounded-xl border-border bg-card shadow-sm">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="text-base font-semibold">Full plan comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Feature</th>
                  <th className="px-4 py-3 text-center font-medium text-muted-foreground">Free</th>
                  <th className="px-4 py-3 text-center font-medium text-primary">Pro</th>
                  <th className="px-4 py-3 text-center font-medium text-muted-foreground">Business</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f, i) => (
                  <tr key={f.label} className={`border-b border-border last:border-0 ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                    <td className="px-4 py-2.5 text-muted-foreground">{f.label}</td>
                    <td className="px-4 py-2.5 text-center"><FeatureValue val={f.free} /></td>
                    <td className="px-4 py-2.5 text-center"><FeatureValue val={f.pro} /></td>
                    <td className="px-4 py-2.5 text-center"><FeatureValue val={f.business} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          All prices in USD. Business plan billed per seat per month. Cancel anytime. <br />
          Questions? Email <a href="mailto:support@taskoru.com" className="underline hover:text-foreground">support@taskoru.com</a>
        </p>
      </main>
    </Shell>
  )
}
